import React, { useState, useEffect } from 'react';
import './Agents.css';

function Agents({ isOpen, onClose }) {
  const [agents, setAgents] = useState([]);
  const [editingAgent, setEditingAgent] = useState(null);
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentDescription, setNewAgentDescription] = useState('');
  const [newAgentSystemPrompt, setNewAgentSystemPrompt] = useState('');
  const [selectedParentId, setSelectedParentId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = () => {
    try {
      const saved = localStorage.getItem('agents');
      if (saved) {
        setAgents(JSON.parse(saved));
      }
    } catch (err) {
      console.error('Error loading agents:', err);
      setError('Failed to load agents');
    }
  };

  const saveAgents = (updatedAgents) => {
    try {
      localStorage.setItem('agents', JSON.stringify(updatedAgents));
      setAgents(updatedAgents);
    } catch (err) {
      console.error('Error saving agents:', err);
      setError('Failed to save agents');
    }
  };

  const handleCreateAgent = () => {
    if (!newAgentName.trim()) {
      setError('Agent name is required');
      return;
    }

    const newAgent = {
      id: `agent-${Date.now()}`,
      name: newAgentName.trim(),
      description: newAgentDescription.trim(),
      systemPrompt: newAgentSystemPrompt.trim(),
      parentId: selectedParentId || null,
      createdAt: new Date().toISOString(),
    };

    const updated = [...agents, newAgent];
    saveAgents(updated);
    
    // Reset form
    setNewAgentName('');
    setNewAgentDescription('');
    setNewAgentSystemPrompt('');
    setSelectedParentId('');
    setError('');
  };

  const handleDeleteAgent = (agentId) => {
    // Check if this agent has children
    const hasChildren = agents.some(a => a.parentId === agentId);
    if (hasChildren) {
      if (!window.confirm('This agent has sub-agents. Deleting it will also delete all its sub-agents. Continue?')) {
        return;
      }
      // Recursively delete children
      const deleteAgentAndChildren = (id) => {
        const childIds = agents.filter(a => a.parentId === id).map(a => a.id);
        childIds.forEach(deleteAgentAndChildren);
        return agents.filter(a => a.id !== id);
      };
      saveAgents(deleteAgentAndChildren(agentId));
    } else {
      if (!window.confirm(`Delete agent "${agents.find(a => a.id === agentId)?.name}"?`)) {
        return;
      }
      saveAgents(agents.filter(a => a.id !== agentId));
    }
  };

  const handleStartEdit = (agent) => {
    setEditingAgent(agent);
  };

  const handleSaveEdit = () => {
    if (!editingAgent.name.trim()) {
      setError('Agent name is required');
      return;
    }

    const updated = agents.map(a => 
      a.id === editingAgent.id ? editingAgent : a
    );
    saveAgents(updated);
    setEditingAgent(null);
    setError('');
  };

  const handleCancelEdit = () => {
    setEditingAgent(null);
    setError('');
  };

  const getAgentHierarchy = () => {
    // Build a tree structure
    const rootAgents = agents.filter(a => !a.parentId);
    const buildTree = (parentId = null) => {
      return agents
        .filter(a => a.parentId === parentId)
        .map(agent => ({
          ...agent,
          children: buildTree(agent.id),
        }));
    };
    return rootAgents.map(agent => ({
      ...agent,
      children: buildTree(agent.id),
    }));
  };

  const renderAgentTree = (agentList, level = 0) => {
    return agentList.map(agent => (
      <div key={agent.id} style={{ marginLeft: `${level * 20}px` }}>
        {editingAgent?.id === agent.id ? (
          <div className="agent-edit-card">
            <input
              type="text"
              value={editingAgent.name}
              onChange={(e) => setEditingAgent({ ...editingAgent, name: e.target.value })}
              placeholder="Agent name"
              className="agent-input"
            />
            <textarea
              value={editingAgent.description}
              onChange={(e) => setEditingAgent({ ...editingAgent, description: e.target.value })}
              placeholder="Description"
              className="agent-input"
              rows="2"
            />
            <textarea
              value={editingAgent.systemPrompt}
              onChange={(e) => setEditingAgent({ ...editingAgent, systemPrompt: e.target.value })}
              placeholder="System prompt for this agent"
              className="agent-input"
              rows="4"
            />
            <div className="agent-edit-actions">
              <button onClick={handleSaveEdit} className="save-agent-button">
                💾 Save
              </button>
              <button onClick={handleCancelEdit} className="cancel-agent-button">
                ❌ Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="agent-card">
            <div className="agent-card-header">
              <div className="agent-info">
                <div className="agent-name">
                  {level > 0 && '└─ '}
                  {agent.name}
                </div>
                {agent.description && (
                  <div className="agent-description">{agent.description}</div>
                )}
              </div>
              <div className="agent-actions">
                <button
                  onClick={() => handleStartEdit(agent)}
                  className="edit-agent-button"
                  title="Edit agent"
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDeleteAgent(agent.id)}
                  className="delete-agent-button"
                  title="Delete agent"
                >
                  🗑️
                </button>
              </div>
            </div>
            {agent.systemPrompt && (
              <div className="agent-system-prompt">
                <strong>System Prompt:</strong> {agent.systemPrompt}
              </div>
            )}
            <div className="agent-metadata">
              <small>ID: {agent.id}</small>
              {agent.children && agent.children.length > 0 && (
                <small> • {agent.children.length} sub-agent(s)</small>
              )}
            </div>
          </div>
        )}
        {agent.children && agent.children.length > 0 && (
          <div className="agent-children">
            {renderAgentTree(agent.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  if (!isOpen) return null;

  const hierarchy = getAgentHierarchy();
  const availableParents = agents.filter(a => !editingAgent || a.id !== editingAgent.id);

  return (
    <div className="agents-overlay" onClick={onClose}>
      <div className="agents-modal" onClick={(e) => e.stopPropagation()}>
        <div className="agents-header">
          <h2>🤖 Agent Hierarchy Manager</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="agents-content">
          {error && <div className="agents-error">{error}</div>}

          {/* Create New Agent Section */}
          <div className="create-agent-section">
            <h3>➕ Create New Agent</h3>
            <input
              type="text"
              value={newAgentName}
              onChange={(e) => setNewAgentName(e.target.value)}
              placeholder="Agent name (required)"
              className="agent-input"
            />
            <textarea
              value={newAgentDescription}
              onChange={(e) => setNewAgentDescription(e.target.value)}
              placeholder="Description (optional)"
              className="agent-input"
              rows="2"
            />
            <textarea
              value={newAgentSystemPrompt}
              onChange={(e) => setNewAgentSystemPrompt(e.target.value)}
              placeholder="System prompt for this agent (optional)"
              className="agent-input"
              rows="4"
            />
            <div className="parent-selector">
              <label>Parent Agent (optional):</label>
              <select
                value={selectedParentId}
                onChange={(e) => setSelectedParentId(e.target.value)}
                className="agent-input"
              >
                <option value="">None (Root Agent)</option>
                {availableParents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleCreateAgent}
              disabled={!newAgentName.trim()}
              className="create-agent-button"
            >
              ➕ Create Agent
            </button>
          </div>

          {/* Agent Hierarchy Display */}
          <div className="agent-hierarchy-section">
            <h3>📊 Agent Hierarchy ({agents.length} total)</h3>
            {agents.length === 0 ? (
              <div className="no-agents">
                <p>📭 No agents created yet</p>
                <p style={{ fontSize: '0.9em', color: '#666' }}>
                  Create your first agent above to enable parent-child chat communication.
                </p>
              </div>
            ) : (
              <div className="agent-tree">
                {renderAgentTree(hierarchy)}
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className="agents-help">
            <h4>💡 How to Use Agents</h4>
            <ul>
              <li>Create agents with specific system prompts for specialized tasks</li>
              <li>Set parent-child relationships to create agent hierarchies</li>
              <li>Parent agents can invoke sub-agents via the "invoke_sub_agent" tool</li>
              <li>Each agent can have its own system prompt and behavior</li>
              <li>Use this to build multi-agent systems for complex workflows</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Agents;
